import { create } from '@bufbuild/protobuf'
import { PlanSubmissionKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  type SubmissionManagementPlanKey,
  SubmissionManagementPlanKeySchema,
} from '@ebalance/proto/bffuser/v1/submission_management_model_pb'
import { generatePath } from 'react-router'

import { parseIsoDate, toProtoDate } from '~/models/dateTime'
import { routePaths } from '~/routes/routePaths'

/**
 * eBalance に提出記録の無い計画の詳細 (AC-PPD-028) の URL クエリ
 * `targetDate` (YYYY-MM-DD) / `submissionEntityCode` を業務キーにする。計画種別は
 * route が需要調達計画に固定する。送り側の契約は
 * `SubmissionManagement/models/planDetailUrl.ts` の `buildNoSubmissionRecordPlanUrl`
 * (AC-PSM-038)。どちらかが欠けている・形式が不正な時は undefined。
 */
export function parseNoSubmissionRecordPlanQuery(
  params: URLSearchParams,
): { planKey: SubmissionManagementPlanKey; targetDate: Date } | undefined {
  const targetDate = parseIsoDate(params.get('targetDate') ?? '')
  const submissionEntityCode = params.get('submissionEntityCode')?.trim() ?? ''
  if (targetDate === undefined || submissionEntityCode === '') return undefined
  return {
    planKey: create(SubmissionManagementPlanKeySchema, {
      kind: PlanSubmissionKind.DEMAND_PROCUREMENT,
      targetDate: toProtoDate(targetDate),
      submissionEntityCode,
    }),
    targetDate,
  }
}

/** 一時ハイライトの query (AC-PPD-022 / AC-PSM-038-1)。版の詳細へ置き換える時に引き継ぐ。 */
const HIGHLIGHT_QUERY_KEYS = [
  'highlightFrom',
  'highlightTo',
  'partner',
  'side',
] as const

/**
 * 業務キーの計画に代表の版があった時 (AC-PPD-029) の行き先: 版の詳細の URL に、
 * 現在のハイライト query だけを引き継ぐ (業務キーの query は持ち込まない)。
 */
export function buildVersionDetailRedirectUrl(
  planSubmissionId: string,
  params: URLSearchParams,
): string {
  const path = generatePath(routePaths.planProcurementDetail.path, {
    id: planSubmissionId,
  })
  const query = new URLSearchParams()
  for (const key of HIGHLIGHT_QUERY_KEYS) {
    const value = params.get(key)
    if (value !== null) query.set(key, value)
  }
  const search = query.toString()
  return search === '' ? path : `${path}?${search}`
}
